import { useEffect, useRef, useState } from 'react';

import PlaylistTrack from './PlaylistTrack';
import styles from './PlaylistTracklist.module.scss';

const PlaylistTracklist = ({
  clips,
  currentLiveViewers,
  isExpanded,
  isPlaying,
  selectedClipId,
  switchClip,
  tracklistRef,
}) => {
  const listRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const selectedIndex = clips.findIndex((clip) => clip.id === selectedClipId);

  // Reset focused index when collapsing the list
  useEffect(() => {
    if (!isExpanded) {
      setFocusedIndex(-1);
    }
  }, [isExpanded]);

  // Handle focus on the list container
  const handleListFocus = (e) => {
    // Only set focus to the first item if the list itself received focus (not its children)
    if (e.target === listRef.current && focusedIndex === -1) {
      // Focus the selected item or first item
      const targetIndex = selectedIndex >= 0 ? selectedIndex : 0;
      setFocusedIndex(targetIndex);

      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        const options = Array.from(listRef.current.children);
        if (options.length > targetIndex) {
          options[targetIndex].focus();
        }
      }, 10);
    }
  };

  const handleKeyDown = (e) => {
    if (!isExpanded) return;

    const options = Array.from(listRef.current.children);
    if (!options.length) return;

    let newIndex = focusedIndex;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        newIndex = focusedIndex < options.length - 1 ? focusedIndex + 1 : 0;
        setFocusedIndex(newIndex);
        options[newIndex].focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        newIndex = focusedIndex > 0 ? focusedIndex - 1 : options.length - 1;
        setFocusedIndex(newIndex);
        options[newIndex].focus();
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        options[0].focus();
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(options.length - 1);
        options[options.length - 1].focus();
        break;
    }
  };

  return (
    <ul
      id="playlist-tracklist"
      className={`${styles.playlistTracklist} ${isExpanded ? styles.expanded : ''}`}
      ref={(el) => {
        if (tracklistRef) tracklistRef.current = el;
        listRef.current = el;
      }}
      role="listbox"
      aria-label="Playlist videos"
      tabIndex={isExpanded && focusedIndex === -1 ? 0 : -1}
      onKeyDown={handleKeyDown}
      onFocus={handleListFocus}
    >
      {clips.map((clip, index) => (
        <PlaylistTrack
          key={clip.id}
          clip={clip}
          currentLiveViewers={currentLiveViewers}
          isPlaying={isPlaying}
          isSelected={selectedClipId === clip.id}
          isFocused={index === focusedIndex}
          switchClip={switchClip}
          tabIndex={index === focusedIndex ? 0 : -1}
        />
      ))}
    </ul>
  );
};

export default PlaylistTracklist;
